// GoLiveVariant.js
// ─────────────────────────────────────────────────────────────────────────
// Decides which "go live" offer the user sees on /recommended-plan:
//   • iOS      → ALWAYS $5/mo (GoLivePayg). Hard lock: na refresh se badalta
//                hai, na flow B ki alternation se, aur na sales agent ke
//                select_offer("trial") se. iOS pe 21-day trial screen dikhni
//                hi nahi chahiye.
//   • Flow A   → ALWAYS $5/mo (GoLivePayg). Fixed default, aur refresh pe bhi
//                wahi — koi alternation nahi.
//   • Flow B   → ALTERNATES by visit count: 1st visit → trial ($20),
//                2nd → $5/mo, 3rd → trial, and so on.
// In sab ke upar ek exception hai: "Upgrade Plan" se aaya user (?from=upgrade)
// — wahan hamesha trial, iOS par bhi.
//
// The chosen variant is published to the call context so the live-call route
// guidance (CallRouteGuide) injects the MATCHING context — $5/mo pricing vs the
// 21-day-trial offer — while the same /recommended-plan URL is showing.
//
// StrictMode-safe: React 18 dev double-invokes state initializers AND remounts
// the whole tree once, which would otherwise bump the counter twice per visit
// (breaking the alternation). We dedupe on a short time window: StrictMode's
// repeat runs happen within milliseconds (so they reuse the already-chosen
// variant), while a genuine new visit — an in-app navigation or a page reload —
// is seconds apart and advances the counter exactly once. Works on reload too
// (unlike a location.key guard, which resets to "default" on every reload).
// ─────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect } from "react";
import GoLivePayg from "./GoLivePayg";
import TrialOffer from "../TrialOffer/TrialOffer";
import detectPlatform from "../../utils/detectPlatform";
import { useGlobalCall } from "../../contexts/GlobalCallContext";
import { useOfferStore } from "../../Store/offerStore";
import { useAgentStore } from "../../Store/agentDetailStore";
import { getVariant, EXPERIMENTS, VARIANT_B, isVariantB } from "../../lib/experiments";
import PageSlide from "../PageSlide/PageSlide";

export const GOLIVE_VARIANT_COUNT_KEY = "goLiveVariantCount";
export const GOLIVE_ACTIVE_VARIANT_KEY = "goLiveActiveVariant"; // "payg" | "trial"
const GOLIVE_LAST_VISIT_AT_KEY = "goLiveLastVisitAt";
// Set right before we leave for Stripe; makes the NEXT visit keep the same
// offer (so returning from checkout doesn't flip $5 ↔ trial). Consumed once.
const GOLIVE_KEEP_ON_RETURN_KEY = "goLiveKeepVariantOnReturn";
// StrictMode double-mount is near-instant; a real visit is far longer apart.
const SAME_VISIT_WINDOW_MS = 1500;
// How long we keep re-checking for the native bridge before giving up. The RN
// shell injects window.ReactNativeWebView within a few hundred ms of a reload;
// 5s is generous and costs nothing (we stop the moment it lands).
const IOS_BRIDGE_WAIT_MS = 5000;
const IOS_BRIDGE_POLL_MS = 200;

// True only on the iOS app surface. detectPlatform() caches the answer in
// localStorage the first time the bridge is seen, so after one successful
// detection every later refresh reads "ios" from the very first render.
const isIosSurface = () => detectPlatform() === "ios";

const parseVoipNumber = (raw) => {
  if (!raw) return "";
  if (Array.isArray(raw)) return raw[0] || "";
  if (typeof raw === "string") {
    if (raw.trim().startsWith("[")) {
      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed[0] || "" : parsed || "";
      } catch {
        return raw;
      }
    }
    return raw;
  }
  return "";
};

const hasAssignedNumber = (agent) => {
  if (!agent) return false;
  return Boolean(parseVoipNumber(agent.voip_numbers));
};

const readLS = (k, d = null) => {
  try {
    return localStorage.getItem(k) ?? d;
  } catch (_e) {
    return d;
  }
};
const writeLS = (k, v) => {
  try {
    localStorage.setItem(k, v);
  } catch (_e) {
    /* ignore */
  }
};
const removeLS = (k) => {
  try {
    localStorage.removeItem(k);
  } catch (_e) {
    /* ignore */
  }
};

// Call right before redirecting to a checkout from the go-live screen, so when
// the user comes back they see the SAME offer instead of the alternated one.
export const markGoLiveCheckoutRedirect = () => writeLS(GOLIVE_KEEP_ON_RETURN_KEY, "1");

const GoLiveVariant = () => {
  const { agentData, getAgentById } = useAgentStore();
  const agentId =
    (typeof sessionStorage !== "undefined" &&
      (sessionStorage.getItem("SelectAgentId") || sessionStorage.getItem("agentId"))) ||
    null;
  const currentAgent = agentData?.agent || (agentId ? getAgentById(agentId)?.agentData?.agent : null);
  const agentHasVoip = hasAssignedNumber(currentAgent);
  const inBFlow = isVariantB();
  const cameFromUpgrade =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("from") === "upgrade";

  // Decide once per genuine visit. A lazy initializer keeps the variant stable
  // across this visit's re-renders; the time-window guard makes it stable across
  // StrictMode's double-invoke + remount too.
  const [variant, setVariant] = useState(() => {
    // "Upgrade Plan" se aaya user hamesha trial dekhta hai — iOS par bhi. Isi
    // liye ye check iOS lock se UPAR hai.
    //
    // B-Flow me number pehle hi assign ho chuka hota hai: wahan bhi 21-day
    // trial ($20) — refresh par kabhi bhi $5 PAYG me flip nahi hona chahiye,
    // warna user setup loop me phas jata hai.
    if (cameFromUpgrade || (inBFlow && agentHasVoip && !isIosSurface())) {
      writeLS(GOLIVE_ACTIVE_VARIANT_KEY, "trial");
      return "trial";
    }

    // iOS: only ever the $5/mo PAYG screen.
    if (isIosSurface()) {
      writeLS(GOLIVE_ACTIVE_VARIANT_KEY, "payg");
      return "payg";
    }

    /*
     * FLOW A → hamesha $5 PAYG.
     *
     * Pehle flow A bhi neeche wali alternation pe chalta tha (1st visit
     * trial $20, 2nd payg $5, 3rd trial…). Do dikkatein thi: landing pe
     * default $20 aata tha, aur har refresh ek nayi visit ginta tha, to
     * offer $20 ↔ $5 flip karta rehta.
     *
     * Ab flow A ka faisla fix hai — na counter badhta hai, na refresh se
     * badalta hai. Alternation sirf flow B ke liye bachi hai.
     *
     * Upar ke do checks jaan-boojh ke pehle hain: iOS pe PAYG dikhana hi
     * nahi hai, aur "Upgrade Plan" se aaya user trial hi dekhega — ye
     * dono flow A me bhi lagoo rehte hain.
     */
    if (!inBFlow) {
      writeLS(GOLIVE_ACTIVE_VARIANT_KEY, "payg");
      return "payg";
    }

    // Returning from a checkout we launched → keep the SAME offer (don't
    // alternate). Consume the flag so it applies only to this one return.
    if (readLS(GOLIVE_KEEP_ON_RETURN_KEY) === "1") {
      removeLS(GOLIVE_KEEP_ON_RETURN_KEY);
      writeLS(GOLIVE_LAST_VISIT_AT_KEY, String(Date.now()));
      return readLS(GOLIVE_ACTIVE_VARIANT_KEY, "trial");
    }

    // Within the same-visit window (StrictMode re-run / remount, or a quick
    // re-render) → reuse the variant we already chose; do NOT bump the counter.
    const now = Date.now();
    const lastAt = parseInt(readLS(GOLIVE_LAST_VISIT_AT_KEY, "0"), 10) || 0;
    if (now - lastAt < SAME_VISIT_WINDOW_MS) {
      return readLS(GOLIVE_ACTIVE_VARIANT_KEY, "trial");
    }

    // A NEW visit → advance the counter and alternate. Trial ($20) is the
    // DEFAULT so it shows first (odd → trial, even → payg).
    writeLS(GOLIVE_LAST_VISIT_AT_KEY, String(now));
    const n = (parseInt(readLS(GOLIVE_VARIANT_COUNT_KEY, "0"), 10) || 0) + 1;
    writeLS(GOLIVE_VARIANT_COUNT_KEY, String(n));
    const v = n % 2 === 1 ? "trial" : "payg";
    writeLS(GOLIVE_ACTIVE_VARIANT_KEY, v);
    return v;
  });

  // The initializer above runs on the FIRST render, and on a reload inside the
  // app the RN bridge (window.ReactNativeWebView) can still be missing at that
  // instant — detectPlatform() then reports "web", so a flow B iOS user could
  // land on the trial. Keep looking for the bridge for a few seconds and snap
  // to PAYG the moment it appears. detectPlatform() caches "ios" in
  // localStorage on that first sighting, so every later refresh is correct
  // from render one.
  useEffect(() => {
    if (variant === "payg" || cameFromUpgrade) return undefined;
    const snapToPayg = () => {
      if (!isIosSurface()) return false;
      writeLS(GOLIVE_ACTIVE_VARIANT_KEY, "payg");
      setVariant("payg");
      return true;
    };
    if (snapToPayg()) return undefined;
    const startedAt = Date.now();
    const timer = setInterval(() => {
      if (snapToPayg() || Date.now() - startedAt >= IOS_BRIDGE_WAIT_MS) {
        clearInterval(timer);
      }
    }, IOS_BRIDGE_POLL_MS);
    return () => clearInterval(timer);
  }, [variant, cameFromUpgrade]);

  // The sales agent's "select_offer" tool can FORCE the offer: 'trial' or 'payg'
  // override the alternation. ('plans' doesn't change the base screen — it just
  // opens the plan picker, handled inside GoLivePayg / TrialOffer.)
  const forcedOffer = useOfferStore((s) => s.forcedOffer);
  // iOS is a HARD LOCK on $5/mo: neither the alternation, a refresh, nor a
  // forced "trial" from the live sales agent may put the trial screen up. The
  // one way out stays ?from=upgrade, which is handled above.
  const iosLocked = isIosSurface() && !cameFromUpgrade;
  const effectiveVariant = iosLocked
    ? "payg"
    : forcedOffer === "trial" || forcedOffer === "payg"
      ? forcedOffer
      : variant;

  // Publish the effective variant to the call context so CallRouteGuide injects
  // the MATCHING agent guidance. Reset on unmount so a later visit starts
  // "undecided" and the guide waits for the fresh choice.
  const { setGoLiveVariant, primeGlobalCall } = useGlobalCall();
  useEffect(() => {
    setGoLiveVariant(effectiveVariant);
    return () => setGoLiveVariant(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveVariant]);

  // Prime the global call banner so that on /recommended-plan the banner shows
  // with "ready" status if no call is actively live.
  useEffect(() => {
    const targetAgent = currentAgent || (agentId ? getAgentById(agentId)?.agentData?.agent : null);
    const targetBusiness = agentData?.business || (agentId ? getAgentById(agentId)?.agentData?.business : null);
    const effId = targetAgent?.agent_id || agentId;
    if (effId && typeof primeGlobalCall === "function") {
      primeGlobalCall({
        agentId: effId,
        agentName: targetAgent?.agentName || sessionStorage.getItem("agentName") || "Sophia",
        agentAvatar: targetAgent?.avatar || targetAgent?.agentAvatar || "/svg/receptionist_profile.png",
        businessName: targetBusiness?.bussinessName || targetBusiness?.businessName || sessionStorage.getItem("businessName") || "",
      });
    }
  }, [currentAgent, agentId, getAgentById, agentData, primeGlobalCall]);

  // Wrap in PageSlide so the offer slides in smoothly. key={effectiveVariant}
  // remounts PageSlide when the agent switches offers (e.g. trial → $5 PAYG)
  // WITHOUT a route change, so the new page slides in each time too.
  return (
    <PageSlide key={effectiveVariant}>
      {effectiveVariant === "trial" ? <TrialOffer /> : <GoLivePayg />}
    </PageSlide>
  );
};

export default GoLiveVariant;
